import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Gift, Users } from 'lucide-react';
import { useCmsContent } from '../hooks/useCmsContent';
import { parseHighlight } from '../utils/richText';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { currencySymbol } from '../lib/currency';

const normalizePricingText = (value = '') =>
  value
    .replace(/\u00f0\u0178\S+/g, '')
    .replace(/\u00e2\u20ac[\u201c\u201d]/g, '-')
    .replace(/\u00c2\u00b7/g, ' - ')
    .replace(/\u00e2\u201a\u00ac/g, ' EUR')
    .replace(/\s{2,}/g, ' ')
    .trim();

// CMS package text is free-form ("1 hour: 30\u20ac"), not a numeric amount + currency
// code, so it can't go through formatMoney(). This keeps the school's admin-authored
// copy but swaps the literal \u20ac for the configured default_currency's symbol (F4) \u2014
// so a currency change doesn't leave stale \u20ac signs silently wrong on this page.
const formatPricingText = (value = '', symbol = '\u20ac') =>
  normalizePricingText(value)
    .replace(/\u00e2\u201a\u00ac/g, symbol)
    .replace(/\bEUR\b/g, symbol)
    .replace(/\u20ac/g, symbol);

const INTENSIVE_IMAGE_MAIN = '/assets/photos/site-updates/flankly.webp';
const INTENSIVE_IMAGE_SECONDARY = '/assets/photos/site-updates/student-results-certificates.webp';
// price/lessons/duration are composed at render time from priceTemplate/lessonsTemplate/
// durationTemplate (see i18n dictionaries) plus these raw amounts — the amounts are
// language-neutral (currency-formatted separately via formatPricingText), only the
// surrounding wording changes per language.
const DEFAULT_PACKAGES = [
  {
    name: 'Private Classes (1:1)',
    label: 'Personalised attention',
    description: 'For fast progress and full personalisation',
    priceAmount: '30€', perStudent: false,
    lessonsAmount: '260€', lessonsSave: '40€',
    durationAmount: '40€',
    features: ['Full personalised attention', 'Flexible schedule', 'Google Meet included', 'Progress tracking', 'Custom materials'],
    popular: false,
    accentColor: 'bg-[#1A2E6E]',
    badgeClass: 'bg-[#E9EEFF] text-[#1A2E6E]',
  },
  {
    name: 'Pair Classes (2 students)',
    label: 'Shared learning',
    description: 'Learn with a friend and stay motivated',
    priceAmount: '22€', perStudent: true,
    lessonsAmount: '200€', lessonsSave: '20€',
    durationAmount: '30€',
    features: ['Learn with a friend', 'Lower cost per student', 'Google Meet included', 'More speaking time', 'Collaborative learning'],
    popular: false,
    accentColor: 'bg-[#4CAF50]',
    badgeClass: 'bg-[#EAF6EB] text-[#2F8A3A]',
  },
  {
    name: 'Trio Classes (3 students)',
    label: 'Small team format',
    description: 'Dynamic, interactive, and affordable',
    priceAmount: '17€', perStudent: true,
    lessonsAmount: '150€', lessonsSave: '20€',
    durationAmount: '24€',
    features: ['Small group dynamic', 'More interaction', 'Google Meet included', 'Peer learning', 'Cost-effective'],
    popular: false,
    accentColor: 'bg-[#F5A623]',
    badgeClass: 'bg-[#FFF4E3] text-[#C77C08]',
  },
  {
    name: 'Small Group (4–6 students)',
    label: 'Best value',
    description: 'Best value and real conversation practice',
    priceAmount: '12€', perStudent: true,
    lessonsAmount: '110€', lessonsSave: '10€',
    durationAmount: '18€',
    features: ['Best value option', 'Real conversation practice', 'Google Meet included', 'Community atmosphere', 'Consistent schedule'],
    popular: true,
    accentColor: 'bg-[#1A2E6E]',
    badgeClass: 'bg-[#E9EEFF] text-[#1A2E6E]',
  }
];

const DEFAULT_OFFERS = [
  {
    icon: Gift,
    title: 'Special Offer',
    description: '20% discount for trio and small group students who purchase 10-class intensive packages.',
    color: 'text-[#D72B2B]',
    bg: 'bg-[#D72B2B]/10'
  },
  {
    icon: Users,
    title: 'Referral Bonus',
    description: 'Refer a friend and receive 10% off your next package renewal after they enrol.',
    color: 'text-[#4CAF50]',
    bg: 'bg-[#4CAF50]/10'
  }
];

const DEFAULT_WHY_STUDENTS = [
  'Practical English for real-life situations abroad',
  'Small groups mean more speaking time',
  'Flexible schedules adapted to your routine',
  'Premium teaching with international qualifications',
];

const PricingPlanCard = ({ pkg, index, horizontal = false }) => {
  const { t } = useLanguage();
  return (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.08 }}
    className={`relative min-w-0 overflow-hidden rounded-2xl border bg-white/90 p-6 shadow-[0_14px_38px_rgba(26,46,110,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_48px_rgba(26,46,110,0.13)] ${
      horizontal ? 'lg:grid lg:grid-cols-[1fr_0.88fr_1fr] lg:items-stretch lg:gap-6 lg:p-7' : 'flex h-full flex-col'
    } ${pkg.popular ? 'border-[#D72B2B]/35 ring-2 ring-[#D72B2B]/15' : 'border-[#1A2E6E]/10'}`}
  >
    <div className={`absolute inset-x-0 top-0 h-1.5 ${pkg.popular ? 'bg-[#D72B2B]' : pkg.accentColor}`} />

    {pkg.popular && (
      <span className="absolute right-4 top-4 rounded-full bg-[#D72B2B] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-white shadow-sm">
        {t('pricing.mostPopular')}
      </span>
    )}

    <div className={horizontal ? 'pt-5 lg:pt-4 lg:pr-4' : 'pt-4'}>
      <span className={`${pkg.badgeClass} inline-flex rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]`}>
        {pkg.label}
      </span>
      <h3 className="mt-5 font-sora text-xl font-bold leading-snug text-[#1A2E6E]">
        {pkg.name}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-[#1A2E6E]/68">
        {pkg.description}
      </p>
    </div>

    <div className={`rounded-xl border border-[#1A2E6E]/10 bg-[#F8F9FC] p-4 ${horizontal ? 'my-6 lg:my-0 lg:flex lg:flex-col lg:justify-center' : 'my-6'}`}>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#1A2E6E]/45">
        {t('pricing.startingAt')}
      </p>
      <p className="font-sora text-xl font-bold leading-tight text-[#1A2E6E]">
        {pkg.price}
      </p>
      <div className="mt-4 space-y-2 border-t border-[#1A2E6E]/10 pt-4">
        <p className="text-sm leading-relaxed text-[#1A2E6E]/68">{pkg.lessons}</p>
        <p className="text-sm leading-relaxed text-[#1A2E6E]/68">{pkg.duration}</p>
      </div>
    </div>

    <div className={horizontal ? 'flex flex-col lg:pt-4' : 'flex flex-1 flex-col'}>
      <ul className={`${horizontal ? 'mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-1' : 'mb-6 flex-1 space-y-3'}`}>
        {pkg.features.map((feature, featureIndex) => (
          <li key={featureIndex} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#4CAF50]/10">
              <Check size={14} className="text-[#4CAF50]" />
            </span>
            <span className="text-sm leading-relaxed text-[#1A2E6E]/72">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all duration-300 ${
          pkg.popular
            ? 'bg-[#D72B2B] text-white shadow-md shadow-[#D72B2B]/20 hover:bg-[#B71C1C]'
            : 'border border-[#1A2E6E]/18 text-[#1A2E6E] hover:border-[#1A2E6E]/35 hover:bg-[#1A2E6E]/5'
        }`}
        data-testid={`pricing-${index}-contact`}
      >
        {t('pricing.getStarted')} <ArrowRight size={16} />
      </Link>
    </div>
  </motion.div>
  );
};

const PricingPlansGrid = ({ packages }) => {
  const featuredIndex = packages.findIndex((pkg) => pkg.popular);
  const featuredPackageIndex = featuredIndex >= 0 ? featuredIndex : 0;
  const featuredPackage = packages[featuredPackageIndex];
  const comparisonPackages = packages
    .map((pkg, index) => ({ pkg, index }))
    .filter((item) => item.index !== featuredPackageIndex);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {featuredPackage && (
        <PricingPlanCard
          key={featuredPackageIndex}
          pkg={featuredPackage}
          index={featuredPackageIndex}
          horizontal
        />
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {comparisonPackages.map(({ pkg, index }) => (
          <PricingPlanCard key={index} pkg={pkg} index={index} />
        ))}
      </div>
    </div>
  );
};

const PLAN_LABEL_KEYS = ['private', 'pair', 'trio', 'group'];

export const Pricing = () => {
  const cms = useCmsContent('pricing');
  const { settings } = usePublicSettings();
  const { t, tList } = useLanguage();
  const symbol = currencySymbol(settings.default_currency || 'EUR');

  const heroBadge = cms.get('hero', 'badge', t('pricing.hero.badge'));
  const heroTitle = cms.get('hero', 'title', t('pricing.hero.title'));
  const heroBody = cms.get('hero', 'body', t('pricing.hero.body'));
  const ctaTitle = cms.get('cta', 'title', t('pricing.ctaSection.title'));
  const ctaBody = cms.get('cta', 'body', t('pricing.ctaSection.body'));
  const ctaPrimary = cms.get('cta', 'cta_primary', t('pricing.ctaSection.primary'));
  const ctaSecondary = cms.get('cta', 'cta_secondary', t('pricing.ctaSection.secondary'));

  const intensiveBadge = cms.get('intensive_plan', 'badge', t('pricing.intensivePlan.badge'));
  const intensiveTitle = cms.get('intensive_plan', 'title', t('pricing.intensivePlan.title'));
  const intensiveSubtitle = cms.get('intensive_plan', 'subtitle', t('pricing.intensivePlan.subtitle'));
  const intensiveGroupPrice = cms.get('intensive_plan', 'group_price', t('pricing.intensivePlan.groupPriceTemplate', { amount: '320€' }));
  const intensivePrivatePrice = cms.get('intensive_plan', 'private_price', t('pricing.intensivePlan.privatePriceTemplate', { amount: '720€' }));
  const intensiveBody = cms.get('intensive_plan', 'body', t('pricing.intensivePlan.body'));
  const intensiveImageMain = cms.get('intensive_plan', 'image_main', INTENSIVE_IMAGE_MAIN);
  const intensiveImageSecondary = cms.get('intensive_plan', 'image_secondary', INTENSIVE_IMAGE_SECONDARY);

  const whyStudentsBadge = cms.get('why_students', 'badge', t('pricing.whyStudentsBadge'));
  const localizedWhyStudents = tList('pricing.whyStudentsList');
  const cmsWhyStudents = cms.getList('why_students_items', []);
  const whyStudents = cmsWhyStudents.length > 0
    ? cmsWhyStudents.map((item, i) => item.text || DEFAULT_WHY_STUDENTS[i] || '')
    : DEFAULT_WHY_STUDENTS.map((text, i) => localizedWhyStudents[i]?.text || text);

  const localizedPkgs = tList('pricing.packagesList');
  const cmsPkgs = cms.getList('packages', []);
  const packages = DEFAULT_PACKAGES.map((pkg, i) => ({
    ...pkg,
    label: PLAN_LABEL_KEYS[i] ? t(`pricing.planLabels.${PLAN_LABEL_KEYS[i]}`) : pkg.label,
    name: normalizePricingText(cmsPkgs[i]?.name || localizedPkgs[i]?.name || pkg.name),
    description: cmsPkgs[i]?.description || localizedPkgs[i]?.description || pkg.description,
    price: formatPricingText(
      cmsPkgs[i]?.price || t(pkg.perStudent ? 'pricing.priceTemplatePerStudent' : 'pricing.priceTemplate', { amount: pkg.priceAmount }),
      symbol
    ),
    lessons: formatPricingText(
      cmsPkgs[i]?.lessons || t('pricing.lessonsTemplate', { amount: pkg.lessonsAmount, save: pkg.lessonsSave }),
      symbol
    ),
    duration: formatPricingText(
      cmsPkgs[i]?.duration || t('pricing.durationTemplate', { amount: pkg.durationAmount }),
      symbol
    ),
    features: cmsPkgs[i]?.features
      ? (typeof cmsPkgs[i].features === 'string'
          ? cmsPkgs[i].features.split('\n').filter(Boolean)
          : cmsPkgs[i].features)
      : (localizedPkgs[i]?.features || pkg.features),
  }));

  const localizedOffers = tList('pricing.offersList');
  const cmsOffers = cms.getList('offers', []);
  const offers = DEFAULT_OFFERS.map((offer, i) => ({
    ...offer,
    title: cmsOffers[i]?.title || localizedOffers[i]?.title || offer.title,
    description: cmsOffers[i]?.description || localizedOffers[i]?.description || offer.description,
  }));

  return (
    <main className="pt-24" data-testid="pricing-page">
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="badge-green mb-4 inline-block">{heroBadge}</span>
            <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
              {parseHighlight(heroTitle)}
            </h1>
            <p className="text-lg text-[#1A2E6E]/70 leading-relaxed">
              {heroBody}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <PricingPlansGrid packages={packages} />
        </div>
      </section>

      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 sm:p-8 md:p-10 lg:p-12 border-t-4 border-t-[#D72B2B] max-w-6xl mx-auto overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(76,175,80,0.08),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(215,43,43,0.08),transparent_40%)]" />
            <div className="relative z-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
              <div className="text-center lg:text-left">
                <span className="badge-red mb-4 inline-block">{normalizePricingText(intensiveBadge)}</span>
                <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-3">{intensiveTitle}</h2>
                <p className="text-[#1A2E6E]/60 text-lg mb-8">{intensiveSubtitle}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="glass-card p-6 border-t-2 border-t-[#4CAF50] text-left">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A2E6E]/45 mb-2">{t('pricing.groupOption')}</p>
                    <p className="font-sora font-bold text-2xl text-[#1A2E6E] mb-1">{formatPricingText(intensiveGroupPrice, symbol)}</p>
                    <p className="text-[#1A2E6E]/60 text-sm">{t('pricing.groupOptionCaption')}</p>
                  </div>
                  <div className="glass-card p-6 border-t-2 border-t-[#D72B2B] text-left">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A2E6E]/45 mb-2">{t('pricing.privateOption')}</p>
                    <p className="font-sora font-bold text-2xl text-[#1A2E6E] mb-1">{formatPricingText(intensivePrivatePrice, symbol)}</p>
                    <p className="text-[#1A2E6E]/60 text-sm">{t('pricing.privateOptionCaption')}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-8 text-left">
                  <div className="rounded-2xl bg-white/70 border border-[#1A2E6E]/8 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A2E6E]/45 mb-1">{t('pricing.focusLabel')}</p>
                    <p className="text-sm text-[#1A2E6E]/75">{t('pricing.focusValue')}</p>
                  </div>
                  <div className="rounded-2xl bg-white/70 border border-[#1A2E6E]/8 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A2E6E]/45 mb-1">{t('pricing.rhythmLabel')}</p>
                    <p className="text-sm text-[#1A2E6E]/75">{t('pricing.rhythmValue')}</p>
                  </div>
                  <div className="rounded-2xl bg-white/70 border border-[#1A2E6E]/8 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A2E6E]/45 mb-1">{t('pricing.idealForLabel')}</p>
                    <p className="text-sm text-[#1A2E6E]/75">{t('pricing.idealForValue')}</p>
                  </div>
                </div>

                <p className="text-[#1A2E6E]/70 text-lg italic">{intensiveBody}</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-5">
                <div className="photo-container h-64 sm:col-span-2 lg:h-80">
                  <img
                    src={intensiveImageMain}
                    alt={t('pricing.intensiveMainImageAlt')}
                    className="photo-fit-card object-[72%_center]"
                  />
                </div>
                <div className="photo-container h-40 lg:h-48">
                  <img src={intensiveImageSecondary} alt={t('pricing.intensiveSecondaryImageAlt')} className="photo-fit-card" />
                </div>
                <div className="glass-card p-5 sm:p-6 flex flex-col justify-center text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#D72B2B] mb-2">{t('pricing.recommendedWhen')}</p>
                  <p className="font-sora font-semibold text-xl text-[#1A2E6E] mb-2">{t('pricing.recommendedWhenTitle')}</p>
                  <p className="text-[#1A2E6E]/65 text-sm leading-relaxed">{t('pricing.recommendedWhenBody')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-warm-texture">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="badge-navy mb-4 inline-block">{normalizePricingText(whyStudentsBadge)}</span>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whyStudents.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-5 flex items-start gap-3"
              >
                <Check size={18} className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
                <span className="text-[#1A2E6E]/80 text-sm leading-relaxed">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-paper-lines">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-orange mb-4 inline-block">{t('pricing.specialOffersBadge')}</span>
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {t('pricing.specialOffersTitle')}
            </h2>
            <p className="text-[#1A2E6E]/70">
              {t('pricing.specialOffersSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {offers.map((offer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 sm:p-8 text-center hover:shadow-lg transition-all"
              >
                <div className={`w-14 h-14 rounded-xl ${offer.bg} flex items-center justify-center mx-auto mb-6`}>
                  <offer.icon size={28} className={offer.color} />
                </div>
                <h3 className="font-sora font-semibold text-lg text-[#1A2E6E] mb-2">
                  {offer.title}
                </h3>
                <p className="text-[#1A2E6E]/70 text-sm">
                  {offer.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-br from-[#1A2E6E] to-[#2A4A8E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-sora text-3xl font-bold text-white mb-4">
              {parseHighlight(ctaTitle)}
            </h2>
            <p className="text-white/80 mb-8">
              {ctaBody}
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
              <Link
                to="/level-test"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D72B2B] px-8 py-3.5 font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B71C1C] hover:shadow-xl sm:w-auto"
                data-testid="pricing-level-test-btn"
              >
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link
                to="/book-trial"
                className="w-full rounded-full border-2 border-white/30 bg-transparent px-8 py-3.5 text-center font-semibold text-white transition-all duration-300 hover:bg-white/10 sm:w-auto"
                data-testid="pricing-book-trial-btn"
              >
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Pricing;
